import React, { useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faEllipsisV, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

import { connect } from 'react-redux';
import {
  addCriticalDate,
  deleteCriticalDate,
  setCriticalDate,
} from 'bundles/apex/deals/show/store/deal/actions';
import { DAY_MONTH_FORMAT } from 'helpers/formatters/date_time_formats';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { formatDate as formatApexDate } from 'bundles/apex/dates/helpers';
import { getFieldReadOnlyMessage, isFieldReadOnly } from 'bundles/apex/deals/helpers/fields';

import ActionsMenu from 'components/actions_menu';
import ClickableIcon from 'components/clickable_icon';
import confirm from 'components/confirm';
import Modal from 'bundles/apex/deals/show/critical_dates/modal';
import ReminderInfo from 'bundles/apex/reminders/info';

import './styles.scss';

export function CriticalDate({
  addCriticalDate,
  checkImmediateRemindersPath,
  criticalDate,
  dateName,
  deal,
  deleteCriticalDate,
  editing,
  fields,
  hasDealColumn,
  setCriticalDate,
  userId,
}) {
  if (!fields[dateName] && !criticalDate.adHoc) {
    // It is possible for custom dates to be present on a deal with no configuration:
    // 1. Add a custom date field to sale/lease deals
    // 2. Add the custom date to a lease deal
    // 3. Edit the custom date field to be for sale deals only
    // Such dates should be ignored
    return null;
  }

  const [showModal, setShowModal] = useState(false);

  const unset = !criticalDate.date && criticalDate.static;
  const missing = unset && fields[dateName].required;

  const onDelete = (e) => {
    e.stopPropagation();

    confirm('Are you sure you want to delete this date?', {
      buttonLabel: 'Delete',
      buttonStyle: 'danger',
      title: `Delete Date?`
    }).then(async () => {
      deleteCriticalDate(dateName);
    });
  };

  const reminders = criticalDate.reminders.filter(reminder => reminder.userId === userId);

  return <React.Fragment>
    <Modal
      addCriticalDate={addCriticalDate}
      checkImmediateRemindersPath={checkImmediateRemindersPath}
      criticalDateName={dateName}
      deal={deal}
      fields={fields}
      reminders={reminders}
      setCriticalDate={setCriticalDate}
      show={showModal}
      onHide={() => setShowModal(false)}
    />

    <tr className="planner-row">
      {editing && <td/>}
      <td className="task-complete">
        <div className="critical-date">
          <FontAwesomeIcon icon={faCalendar} inverse />
        </div>
      </td>
      <td className={classNames({ 'reminder-info-cell': reminders.length > 0 })}>
        {reminders.length ? (
          <ReminderInfo
            reminders={reminders}
            onClick={() => setShowModal(true)}
          />
        ) : <div />}
      </td>
      <td className="planner-table-date">
        <div className="critical-date-info">
          {missing && <FontAwesomeIcon className="mr-2" icon={faExclamationCircle} />}
          <div>
            <div className="d-flex align-items-center">
              {criticalDate && criticalDate.date
                ? formatDate(criticalDate.date, DAY_MONTH_FORMAT).toUpperCase()
                : 'TBD'}
            </div>
            {!criticalDate.static && (
              <div className="task-deadline-relative mt-1">
                {formatApexDate(deal, fields, criticalDate, false)}
              </div>
            )}
          </div>
        </div>
      </td>
      { hasDealColumn && <td><div><a href={`${deal.path}/planner`}>{deal.displayName}</a></div></td> }
      <td>
        <div>{criticalDate.label || fields[dateName].label}</div>
      </td>
      <td><div /></td>
      <td><div /></td>
      <td width="0">
        <div>
          <ActionsMenu
            disableDelete={unset || isFieldReadOnly(dateName, deal)}
            disableDeleteMessage={getFieldReadOnlyMessage(dateName)}
            icon={<ClickableIcon icon={faEllipsisV}/>}
            id={dateName}
            onDelete={onDelete}
            onShowModal={() => setShowModal(true)}
          />
        </div>
      </td>
    </tr>
  </React.Fragment>;
}

CriticalDate.propTypes = {
  addCriticalDate: PropTypes.func.isRequired,
  checkImmediateRemindersPath: PropTypes.string,
  criticalDate: PropTypes.shape({
    adHoc: PropTypes.bool,
    date: PropTypes.string,
    static: PropTypes.bool,
  }).isRequired,
  dateName: PropTypes.string.isRequired,
  deal: PropTypes.object.isRequired,
  deleteCriticalDate: PropTypes.func.isRequired,
  editing: PropTypes.bool,
  fields: PropTypes.object.isRequired,
  hasDealColumn: PropTypes.bool,
  setCriticalDate: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
};

const mapStateToProps = state => ({
  checkImmediateRemindersPath: state.paths.checkUpdateForImmediateReminders,
  userId: state.forUser.id,
});

const mapDispatchToProps = {
  addCriticalDate,
  deleteCriticalDate,
  setCriticalDate,
};

export default connect(mapStateToProps, mapDispatchToProps)(CriticalDate);
